<template>
  <div class="request-element">
    <div class="request-header">
      <span class="request-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="1 4 1 10 7 10"></polyline>
          <polyline points="23 20 23 14 17 14"></polyline>
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
        </svg>
        <span>Requête API</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="request-content">
     
      <div class="form-group">
        <label>Requête</label>
        <div class="request-input-group">
          <select 
            v-model="localElement.method" 
            class="method-select" 
            :class="methodClass"
            @change="saveChanges('method')"
          >
          
            <option value="POST">POST</option>
          </select>
          <input 
            type="text" 
            v-model="localElement.url" 
            class="url-input" 
            placeholder="https://api.example.com"
            @blur="saveChanges('url')"
            @keydown.enter="$event.target.blur()"
          />
        </div>
      </div>
      
      <div class="form-group">
        <label>Variables</label>
        
        <VariableSelector
          v-model="selectedVariables"
          :variables="variables"
          :multiple="true"
          :return-object="true"
          placeholder="Rechercher et sélectionner des variables..."
          @change="updateDatas"
        />
      </div>
      <div class="form-group toggle-group">
        <label class="toggle-label">
          <div class="toggle-switch">
            <input 
              type="checkbox" 
              v-model="localElement.wait_response" 
              @change="saveChanges('wait_response')"
              class="toggle-input"
            />
            <span class="toggle-slider"></span>
          </div>
          <span class="toggle-text">Attendre le retour de la requête avant de continuer</span>
        </label>
      </div>
      
      <!-- Indicateur de sauvegarde -->
      <div v-if="isSaving" class="saving-indicator">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spin">
          <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
        </svg>
        <span>Sauvegarde en cours...</span>
      </div>
      
      <div v-if="lastSaved" class="last-saved-indicator">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <span>Dernière sauvegarde : {{ formatLastSaved }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { axiosPeelo } from '@/utils/axios'
import VariableSelector from '@/components/common/VariableSelector.vue'

export default {
  name: 'RequestElement',
  components: {
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    idBot: {
      type: String,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localElement: {
        method: this.element.method || 'POST',
        url: this.element.url || '',
        datas: this.element.datas || [],
        wait_response: this.element.wait_response || false
      },
      selectedVariables: [],
      isSaving: false,
      lastSaved: null
    }
  },
  created() {
    this.initializeSelectedVariables();
  },
  watch: {
    variables: {
      immediate: true,
      handler(newVariables) {
        this.initializeSelectedVariables();
      }
    },
    element: {
      deep: true,
      handler(newElement) {
        this.localElement = {
          method: newElement.method || 'POST',
          url: newElement.url || '',
          datas: newElement.datas || [],
          wait_response: newElement.wait_response || false
        };
        this.initializeSelectedVariables();
      }
    }
  },
  computed: {
    methodClass() {
      const method = (this.element.method || 'POST').toUpperCase();
      switch (method) {
        case 'GET':
          return 'method-get';
        case 'POST':
          return 'method-post';
        case 'PUT':
          return 'method-put';
        case 'DELETE':
          return 'method-delete';
        default:
          return 'method-other';
      }
    },
    formatLastSaved() {
      if (this.lastSaved) {
        const date = new Date(this.lastSaved);
        return date.toLocaleString();
      }
      return 'Aucune sauvegarde effectuée';
    }
  },
  methods: {
    initializeSelectedVariables() {
      // Initialiser datas comme array si c'est undefined ou pas un array
      if (!this.localElement.datas || !Array.isArray(this.localElement.datas)) {
        this.localElement.datas = [];
      }
      
      // Synchronise selectedVariables avec les ids présents dans element.datas
      if (this.variables && this.variables.length > 0) {
        this.selectedVariables = this.variables.filter(v => 
          this.localElement.datas.includes(v.id)
        );
      } else {
        this.selectedVariables = [];
      }
    },
    
    saveChanges(field) {
       
      this.isSaving = true;
      this.lastSaved = new Date();
      
      // Nettoyer l'objet pour ne garder que datas, method, url, etc.
      const { datas, method, url, wait_response } = this.localElement;
      const updatedElement = { ...this.element, datas, method, url, wait_response };
      
      
      // Émettre l'événement update vers le parent pour mettre à jour le flow
      this.$emit('update', { ...updatedElement });
      
      // Simuler une sauvegarde (remplacer par vraie API si nécessaire)
      setTimeout(() => {
         this.isSaving = false;
      }, 300);
    },
    
    updateDatas() {
      // Mettre à jour directement les datas avec les IDs sélectionnés
      const variableIds = this.selectedVariables.map(v => v.id);
      
      // Garder seulement "last_input" s'il existe, et ajouter les variables
      const hasLastInput = this.element.datas && this.element.datas.includes('last_input');
      this.localElement.datas = hasLastInput ? ['last_input', ...variableIds] : [...variableIds];
      
      // Sauvegarder
      this.saveChanges('datas');
    },
    
  }
}
</script>
<style scoped>
.request-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.request-element:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.request-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo dark green */
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.request-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #DAFFD1; /* Peelo light green */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transform: scale(1.05);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.request-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.request-input-group {
  display: flex;
  gap: 10px;
}

.method-select {
  padding: 10px 14px;
  border-radius: 8px;
  font-weight: 700;
  border: 1px solid transparent;
  outline: none;
  cursor: pointer;
  appearance: none;
}

.method-get { background: rgba(59, 130, 246, 0.1); color: #2563eb; border-color: rgba(59, 130, 246, 0.2); }
.method-post { background: rgba(37, 211, 102, 0.1); color: #059669; border-color: rgba(37, 211, 102, 0.2); }
.method-put { background: rgba(245, 158, 11, 0.1); color: #d97706; border-color: rgba(245, 158, 11, 0.2); }
.method-delete { background: rgba(239, 68, 68, 0.1); color: #dc2626; border-color: rgba(239, 68, 68, 0.2); }
.method-other { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }

.url-input {
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #334155;
  background: #ffffff;
  outline: none;
  transition: all 0.2s;
  font-family: monospace;
}

.url-input:focus {
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(218, 255, 209, 0.4);
}

/* Toggle Switch Styles */
.toggle-group {
  background: #f8fafc;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: .4s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.toggle-input:checked + .toggle-slider {
  background-color: #143028;
}

.toggle-input:focus + .toggle-slider {
  box-shadow: 0 0 1px #143028;
}

.toggle-input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.toggle-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: #475569;
}

/* Indicators */
.saving-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(218, 255, 209, 0.2);
  border: 1px solid rgba(20, 48, 40, 0.1);
  border-radius: 8px;
  color: #143028;
  font-size: 0.85rem;
  font-weight: 500;
}

.spin {
  animation: spin 1s linear infinite;
}

.last-saved-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
